import * as React from 'react';
import { useField } from 'react-final-form';
import { useTranslation } from 'react-i18next';
import Checkbox from '@mui/material/Checkbox';
import { FormControlLabel } from '@mui/material';
import { isValidNumber } from 'libphonenumber-js';

import Input from '../../../../../../Input';
import PhoneInput from '../../../../../../PhoneInput';
import { useTheme } from '../../../../../../theme';
import { Tooltip } from '../../../../../../BaseComponents';
import { emailRegex } from '../../../../../../utils';

interface Props {
	name: string;
	type: 'text' | 'email' | 'phone' | 'comment' | 'checkbox';
	required?: boolean;
	label: React.ReactElement | string;
	className?: string;
	displayInitialValueAsLocalNumber?: boolean;
}

const Field: React.FC<Props> = props => {
	const { t } = useTranslation('Exchange');
	const theme = useTheme('Exchange').CreateRequest;

	const field = useField(props.name, {
		type: props.type === 'checkbox' ? 'checkbox' : 'text',
		validate: value => {
			if (props.type === 'email' && !emailRegex.test(value)) {
				return t('Please enter a valid email');
			} else if (props.type === 'phone' && !isValidNumber(value || '')) {
				return t('Please enter a valid phone');
			} else if (props.type === 'comment' && !value && props.required) {
				return t('Required');
			} else if (props.type === 'checkbox' && !value) {
				return true;
			}

			return undefined;
		}
	});

	if (props.type === 'phone') {
		return (
			<PhoneInput
				value={field.input.value}
				textFieldsProps={{
					...field.input,
					label: props.label,
					error: !!field.meta.error && field.meta.touched,
					helperText: field.meta.touched && field.meta.error
				}}
				displayInitialValueAsLocalNumber={props.displayInitialValueAsLocalNumber}
				className={props.className}
			/>
		);
	}

	if (props.type === 'checkbox') {
		return (
			<FormControlLabel
				control={
					<Tooltip
						open={field.meta.submitFailed && !field.input.value}
						title={t('Please, read the rules of service and accept it')}
					>
						<Checkbox
							classes={{
								checked: theme.button_checked,
								root: theme.button
							}}
							checked={field.input.checked}
							inputProps={{
								...field.input
							}}
						/>
					</Tooltip>
				}
				label={props.label}
			/>
		);
	}

	return (
		<Input
			label={props.label as string}
			value={field.input.value}
			TextFieldProps={{
				...field.input,
				error: !!field.meta.error && field.meta.touched,
				helperText: field.meta.touched && field.meta.error
			}}
			className={props.className}
			absoluteHintPosition={true}
		/>
	);
};

export default Field;
